import { useCallback } from 'react';
import { Point, Line, Polygon, ModeSettings, GlobalContext, Shape } from '../../../context/context';
import { calculateDistance } from '../utils';
import { processGTFSFile } from '../processors';
import { DEFAULT_SIMULATION_DATE } from '../../../config/constants';
import { formatDateAsWallTime } from '../../../utils/dateTimeUtils';

interface MapHandlerProps {
    mode: string;
    modeSettings: ModeSettings;
    setModeSettings: (settings: ModeSettings | ((prev: ModeSettings) => ModeSettings)) => void;
    addPoint: (point: Point) => void;
    addPolygon: (points: [number, number][]) => string | undefined;
    addLine: (points: [number, number][]) => void;
    setSelectedShapes: (shapes: string[]) => void;
    global: GlobalContext;
    getPointDraft?: (coordinates: [number, number]) => Partial<Point>;
    onPolygonCompleted?: () => void;
}

export function useMapClickHandler({
    mode,
    modeSettings,
    setModeSettings,
    addPoint,
    addPolygon,
    addLine,
    setSelectedShapes,
    global,
    getPointDraft,
    onPolygonCompleted
}: MapHandlerProps) {
    return useCallback((e: { latlng: { lat: number; lng: number }, originalEvent: { defaultPrevented: boolean } }) => {
        const { lat, lng } = e.latlng;

        // Clear selection when clicking on the map (not on a shape) in Select mode
        if (mode === 'Select' && !e.originalEvent.defaultPrevented) {
            setSelectedShapes([]);
            setModeSettings(prev => ({
                ...prev,
                editingPolygonId: null,
                editingVertexIndex: null
            }));
        }

        if (mode === 'Add Points') {
            const pointDraft = getPointDraft?.([lat, lng]);
            const newPoint: Point = {
                id: crypto.randomUUID(),
                type: 'point',
                name: pointDraft?.name ?? 'New Point',
                description: '',
                colour: '#3388ff',
                icon: 'default',
                size: 24,
                isLocked: false,
                coordinates: [lat, lng],
                ...pointDraft
            };
            addPoint(newPoint);
        }

        if (mode === 'Draw Polygon') {
            const points = modeSettings.drawingPolygonPoints;
            
            // Check if we have points and if the click is near the first point
            if (points.length >= 2) {
                const firstPoint = points[0];
                const distanceToFirst = calculateDistance([lat, lng], firstPoint);
                const lastPoint = points[points.length - 1];
                const distanceToLast = calculateDistance([lat, lng], lastPoint);
                // Use a slightly more forgiving close radius so users do not need to hit the vertex dead-center.
                const clickedNearFirstPoint = distanceToFirst < 0.005;
                // Also allow users to click back onto the current end-point to finish, matching "close on last point" UX.
                const clickedNearLastPoint = points.length >= 3 && distanceToLast < 0.0012;
                if (clickedNearFirstPoint || clickedNearLastPoint) {
                    // Complete the polygon
                    addPolygon(points);
                    setModeSettings(prev => ({
                        ...prev,
                        drawingPolygonPoints: []
                    }));
                    onPolygonCompleted?.();
                    return;
                }
            }
            
            // If not near first point, add the new point
            setModeSettings(prev => ({
                ...prev,
                drawingPolygonPoints: [...prev.drawingPolygonPoints, [lat, lng]]
            }));
        }

        // Draw Trip mode
        if (mode === 'Draw Trip') {
            const { tripOriginPoint } = modeSettings as any;
            const coordinates: [number, number] = [lat, lng];
            if (!tripOriginPoint) {
                // First click: create origin point
                const originPoint: Point = {
                    id: crypto.randomUUID(),
                    type: 'point',
                    name: 'Origin',
                    description: '',
                    coordinates,
                    colour: '#FF0000',
                    icon: 'location',
                    size: 24,
                    isLocked: false
                };
                addPoint(originPoint);
                setSelectedShapes([originPoint.id]);
                setModeSettings((prev: any) => ({
                    ...prev,
                    tripOriginPoint: originPoint,
                    drawingLinePoints: []
                }));
            } else {
                // Second click: batch add destination point and trip line
                const destPoint: Point = {
                    id: crypto.randomUUID(),
                    type: 'point',
                    name: 'Destination',
                    description: '',
                    coordinates,
                    colour: '#00FF00',
                    icon: 'location',
                    size: 24,
                    isLocked: false
                };
                destPoint.layerId = tripOriginPoint.layerId ?? undefined;

                // Generate a random request_time between 6:00am and 10:00pm on the default simulation date, in 15-min increments
                const baseDate = new Date(`${DEFAULT_SIMULATION_DATE}T06:00:00`);
                const randomIncrement = Math.floor(Math.random() * 57); // 0 to 56 (6:00am through 21:45)
                baseDate.setMinutes(baseDate.getMinutes() + randomIncrement * 15);
                const requestTime = formatDateAsWallTime(baseDate);

                const line: Line = {
                    id: crypto.randomUUID(),
                    type: 'line',
                    name: 'Trip',
                    description: '',
                    coordinates: [tripOriginPoint.coordinates, coordinates],
                    colour: '#0000FF',
                    opacity: '1',
                    borderColour: '#0000FF',
                    borderWidth: 2,
                    isLocked: false,
                    originPointId: tripOriginPoint.id,
                    destPointId: destPoint.id,
                    arrowStyle: 'arrow',
                    ride_id: undefined,
                    user_id: undefined,
                    standard_seat: 1,
                    wheelchair: 0,
                    request_type: 'arrival_fixed',
                    request_time: requestTime,
                } as any;
                line.ride_id = line.id;
                line.user_id = destPoint.id;

                // Batch update: add both destPoint and line to the layer
                const layerId = tripOriginPoint.layerId ?? global.activeLayerId ?? global.mapLayers[global.mapLayers.length - 1]?.layerId;
                const layer = global.mapLayers.find((l: any) => l.layerId === layerId);
                if (layer) {
                    const newShapes = [...layer.shapes, destPoint, line];
                    const newLayer = { ...layer, shapes: newShapes };
                    global.setLayers((prev: any) => prev.map((l: any) => l.layerId === layerId ? newLayer : l));
                }

                setSelectedShapes([line.id]);
                // Reset for next trip
                setModeSettings((prev: any) => ({
                    ...prev,
                    tripOriginPoint: null,
                    drawingLinePoints: []
                }));
            }
            return;
        }

        if (mode === 'Draw Line') {
            const currentPoint: [number, number] = [lat, lng];
            const points = modeSettings.drawingLinePoints;

            // Check if we're clicking near the last point to complete the line
            if (points.length >= 2) {
                const lastPoint = points[points.length - 1];
                const closeToLastPoint = calculateDistance(currentPoint, lastPoint) < 0.002;

                if (closeToLastPoint) {
                    addLine(points);
                    setModeSettings(prev => ({
                        ...prev,
                        drawingLinePoints: []
                    }));
                    return;
                }
            }

            setModeSettings(prev => ({
                ...prev,
                drawingLinePoints: [...prev.drawingLinePoints, [lat, lng]]
            }));
        }
    }, [mode, modeSettings, setModeSettings, addPoint, addPolygon, addLine, setSelectedShapes, global, getPointDraft, onPolygonCompleted]);
}

export function useContextMenuHandlers({ editLayer, activeLayerId, mapLayers }: { 
    editLayer: (layer: any, id: number) => void,
    activeLayerId: number | null,
    mapLayers: any[]
}) {
    return {
        handlePointContextMenu: (event: CustomEvent) => ({
            x: event.detail.x,
            y: event.detail.y,
            point: event.detail.point
        }),
        handlePolygonContextMenu: (event: CustomEvent) => ({
            x: event.detail.x,
            y: event.detail.y,
            polygon: event.detail.polygon
        }),
        handleLineContextMenu: (event: CustomEvent) => ({
            x: event.detail.x,
            y: event.detail.y,
            line: event.detail.line
        }),
        handleDeleteShape: (shapeId: string) => {
            if (activeLayerId === null) return;
            
            const layer = mapLayers.find(l => l.layerId === activeLayerId);
            if (layer) {
                const updatedShapes = layer.shapes.filter((shape: Shape) => shape.id !== shapeId);
                const updatedLayer = {
                    ...layer,
                    shapes: updatedShapes
                };
                editLayer(updatedLayer, activeLayerId);
            }
        }
    };
}

export function useDragDropHandlers({
    setIsProcessingGTFS,
    setGtfsError,
    setDroppedFile,
    setIsImportTripsModalOpen,
    setIsDropActive,
    handleImportTrips,
    handleImportKMLShapes,
    global,
    editLayer,
    setShouldFitBounds
}: {
    setIsProcessingGTFS: (value: boolean) => void;
    setGtfsError: (error: string | null) => void;
    setDroppedFile: (file: any | null) => void;
    setIsImportTripsModalOpen: (value: boolean) => void;
    setIsDropActive: (value: boolean) => void;
    handleImportTrips: (points: Point[], lines: Line[], props: { global: GlobalContext; editLayer: (layer: any, id: number) => void; setShouldFitBounds: (value: boolean) => void }) => void;
    handleImportKMLShapes: (shapes: (Point | Line | Polygon)[], props: { global: GlobalContext; editLayer: (layer: any, id: number) => void; setShouldFitBounds: (value: boolean) => void }) => void;
    global: GlobalContext;
    editLayer: (layer: any, id: number) => void;
    setShouldFitBounds: (value: boolean) => void;
}) {
    return {
        processGTFSFile: async (file: File) => {
            setIsProcessingGTFS(true);
            setGtfsError(null);
            try {
                const { points, lines } = await processGTFSFile(file);
                handleImportTrips(points, lines, { global, editLayer, setShouldFitBounds });
                setIsImportTripsModalOpen(false);
            } catch (err) {
                setGtfsError('Error processing GTFS file: ' + (err as Error).message);
            } finally {
                setIsProcessingGTFS(false);
                setDroppedFile(null);
                setIsDropActive(false);
            }
        }
    };
} 
